/**
 * InsightsPage — Scene analytics and insights for the web app.
 *
 * Shows:
 * - Total events, entities, assets stats
 * - Most booked artists
 * - Top venues
 * - Event frequency over time
 */

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAuth } from '@workos-inc/authkit-react';
import {
  LayoutGrid,
  Sparkles,
  LogOut,
  Loader2,
  CalendarDays,
  Users,
  MapPin,
  Star,
  TrendingUp,
  ArrowLeft,
  Image as ImageIcon,
  Layers,
  BarChart2,
} from 'lucide-react';

export function InsightsPage() {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const stats = useQuery(api.dashboard.getStats);
  const allEvents = useQuery(api.events.list, {});
  const allEntities = useQuery(api.entities.list, {});

  // Compute insights from data
  const insights = useMemo(() => {
    if (!allEvents || !allEntities) return null;

    // Most booked artists (entities appearing in event lineups)
    const artistCounts: Record<string, { name: string; count: number }> = {};
    for (const event of allEvents) {
      const meta = (event.metadata ?? {}) as Record<string, unknown>;
      const lineup = (meta.lineup as string[]) ?? [];
      for (const entityId of lineup) {
        const entity = allEntities.find((e) => e._id === entityId);
        if (entity) {
          if (!artistCounts[entityId]) {
            artistCounts[entityId] = { name: entity.name, count: 0 };
          }
          artistCounts[entityId].count++;
        }
      }
    }
    const topArtists = Object.values(artistCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Top venues
    const venueCounts: Record<string, { name: string; count: number }> = {};
    for (const event of allEvents) {
      const meta = (event.metadata ?? {}) as Record<string, unknown>;
      const venueId = meta.venueId as string | null;
      if (venueId) {
        const venue = allEntities.find((e) => e._id === venueId);
        if (venue) {
          if (!venueCounts[venueId]) {
            venueCounts[venueId] = { name: venue.name, count: 0 };
          }
          venueCounts[venueId].count++;
        }
      }
    }
    const topVenues = Object.values(venueCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Events by month
    const monthCounts: Record<string, number> = {};
    for (const event of allEvents) {
      const meta = (event.metadata ?? {}) as Record<string, unknown>;
      const dateStr = meta.date as string | null;
      if (dateStr) {
        const d = new Date(dateStr);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthCounts[key] = (monthCounts[key] ?? 0) + 1;
      }
    }
    const eventsByMonth = Object.entries(monthCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6);

    return { topArtists, topVenues, eventsByMonth };
  }, [allEvents, allEntities]);

  return (
    <div className="min-h-screen bg-background">
      {/* ─── Header ─── */}
      <header className="border-b border-border/50 sticky top-0 bg-background/95 backdrop-blur-sm z-10">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/eventtools-logo.svg" alt="Event.Tools" className="h-7 w-auto" />
            <span className="font-semibold text-foreground">Event.Tools</span>
          </Link>

          <nav className="flex items-center gap-1">
            <Link
              to="/app/dashboard"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              <LayoutGrid className="w-4 h-4" />
              Dashboard
            </Link>
            <Link
              to="/app/editor"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Editor
            </Link>
            <span className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-primary/10 text-primary font-medium">
              <BarChart2 className="w-4 h-4" />
              Insights
            </span>
          </nav>

          <button
            onClick={() => void signOut()}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-sm"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* ─── Main ─── */}
      <main className="container mx-auto px-6 py-8 max-w-4xl space-y-8">
        <button
          onClick={() => navigate('/app/dashboard')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Dashboard
        </button>

        <div>
          <h1 className="text-2xl font-bold tracking-tight">Scene Insights</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Analytics and trends from your event history.
          </p>
        </div>

        {/* Loading */}
        {(stats === undefined || !insights) && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        {stats && insights && (
          <>
            {/* Stats Overview */}
            <section>
              <h2 className="text-base font-semibold mb-3">Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={CalendarDays} label="Total Events" value={stats.totalEvents} />
                <StatCard icon={Users} label="Entities" value={stats.totalEntities} />
                <StatCard icon={ImageIcon} label="Assets" value={stats.totalAssets} />
                <StatCard icon={Layers} label="Projects" value={stats.totalProjects} />
              </div>
            </section>

            {/* Top Artists */}
            {insights.topArtists.length > 0 && (
              <section>
                <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#25a48f]" />
                  Most Booked Artists
                </h2>
                <div className="space-y-2">
                  {insights.topArtists.map((artist, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between px-4 py-3 rounded-xl border border-border bg-card"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-muted-foreground w-6">
                          #{idx + 1}
                        </span>
                        <span className="text-sm font-medium">{artist.name}</span>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#25a48f]/10 text-[#25a48f] font-medium">
                        {artist.count} event{artist.count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Top Venues */}
            {insights.topVenues.length > 0 && (
              <section>
                <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-purple-600" />
                  Top Venues
                </h2>
                <div className="space-y-2">
                  {insights.topVenues.map((venue, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between px-4 py-3 rounded-xl border border-border bg-card"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-muted-foreground w-6">
                          #{idx + 1}
                        </span>
                        <span className="text-sm font-medium">{venue.name}</span>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-600/10 text-purple-600 font-medium">
                        {venue.count} event{venue.count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Events by Month */}
            {insights.eventsByMonth.length > 0 && (
              <section>
                <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#ef5228]" />
                  Event Frequency
                </h2>
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-end gap-2 h-32">
                    {insights.eventsByMonth.map(([month, count]) => {
                      const maxCount = Math.max(...insights.eventsByMonth.map(([, c]) => c));
                      const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                      return (
                        <div key={month} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-xs font-medium">{count}</span>
                          <div
                            className="w-full rounded-t-md bg-[#ef5228]/80 min-h-[4px] transition-all"
                            style={{ height: `${height}%` }}
                          />
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(month + '-01').toLocaleDateString('en-US', {
                              month: 'short',
                            })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            {/* Empty state */}
            {insights.topArtists.length === 0 &&
              insights.topVenues.length === 0 &&
              insights.eventsByMonth.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center">
                    <TrendingUp className="w-10 h-10 text-muted-foreground/40" />
                  </div>
                  <h2 className="text-lg font-semibold">Not enough data yet</h2>
                  <p className="text-sm text-muted-foreground text-center max-w-sm">
                    Create more events and add lineups to unlock scene insights and analytics.
                  </p>
                </div>
              )}
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}
